/**
 * Authentication service types and interfaces
 */

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  /** Profile photo URL; may be returned by GET /me alongside or instead of `avatar`. */
  profileImage?: string;
  role: UserRole | string;
  companyId?: string;
  isActive: boolean;
  isEmailVerified: boolean;
  sellerStatus?: SellerStatus;
  /** For seller roles: true if profile and company required fields (excluding social links) are filled */
  profileCompleteWithoutSocial?: boolean;
  /** For seller roles: 0-100. At 80%+ can access dashboard, analytics, chat. */
  profileCompletionPercentage?: number;
  /** For seller roles: true if membership plan selected (onboarding Step 2). */
  hasMembershipPlanSelected?: boolean;
  /** For seller roles: true if storefront is complete (onboarding Step 3). */
  storefrontComplete?: boolean;
  /** STAFF-only modules from GET /me. */
  allowedModules?: string[];
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
}

export enum UserRole {
  ADMIN = "admin",
  BUYER = "buyer",
  SELLER = "seller",
  MANAGER = "manager",
}

export enum SellerStatus {
  PENDING_APPROVAL = "PENDING_APPROVAL",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  SUSPENDED = "SUSPENDED",
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyName?: string;
  role?: UserRole | "buyer" | "seller" | "both";
  phone?: string;
  country?: string;
  captchaToken?: string;
  pricePlanId?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatar?: File;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginResponse extends AuthResponse {
  message: string;
  success: boolean;
}

export interface RegisterResponse extends AuthResponse {
  message: string;
  success: boolean;
}

export interface LogoutResponse {
  message: string;
  success: boolean;
}

export interface ProfileResponse {
  user: User;
  message: string;
  success: boolean;
}

export interface UpdateProfileResponse {
  user: User;
  message: string;
  success: boolean;
}

export interface ChangePasswordResponse {
  message: string;
  success: boolean;
}

// User status types
export type UserStatus = "not_registered" | "pending_registration" | "active";

export interface MeResponse {
  status: UserStatus;
  user?: User;
  message?: string;
  success: boolean;
}

export interface SyncRequest {
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  phone?: string;
  role?: UserRole;
}

export interface SyncResponse {
  user: User;
  message: string;
  success: boolean;
}
